import React, { forwardRef } from 'react';
import { getFrequencyLevel } from '../utils/deckUtils';

const FlashCard = forwardRef(({
  currentCard,
  currentIndex,
  deckLength,
  isFlipped,
  isDragging,
  dragX,
  swipeDirection,
  isDarkMode,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
}, ref) => {
  // Calculate visual feedback
  const rotation = dragX * 0.1;
  const opacity = Math.max(0.5, 1 - Math.abs(dragX) / 300);

  const getSwipeIndicator = () => {
    if (dragX > 50) return { text: '记住了', color: '#22c55e', opacity: Math.min(1, (dragX - 50) / 100) };
    if (dragX < -50) return { text: '再学习', color: '#ef4444', opacity: Math.min(1, (-dragX - 50) / 100) };
    return null;
  };

  const indicator = getSwipeIndicator();
  const freq = getFrequencyLevel(currentCard.originalIndex);

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        cursor: 'grab',
        touchAction: 'none',
        transformStyle: 'preserve-3d',
        transition: isDragging ? 'none' : 'transform 0.3s ease, opacity 0.3s ease',
        transform: swipeDirection
          ? `translateX(${swipeDirection === 'right' ? '400px' : '-400px'}) rotate(${swipeDirection === 'right' ? '30deg' : '-30deg'})`
          : `translateX(${dragX}px) rotate(${rotation}deg) ${isFlipped ? 'rotateY(180deg)' : ''}`,
        opacity: swipeDirection ? 0 : opacity,
      }}
    >
      {/* Front of card */}
      <div style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        backfaceVisibility: 'hidden',
        background: 'linear-gradient(145deg, #f8fafc, #e2e8f0)',
        borderRadius: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 25px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.8)',
        border: '1px solid rgba(255,255,255,0.3)',
      }}>
        {/* Swipe Indicator Overlay */}
        {indicator && (
          <div style={{
            position: 'absolute',
            top: '20px',
            left: dragX > 0 ? '20px' : 'auto',
            right: dragX < 0 ? '20px' : 'auto',
            padding: '8px 16px',
            background: indicator.color,
            color: 'white',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: 'bold',
            opacity: indicator.opacity,
            transform: `rotate(${dragX > 0 ? '-15deg' : '15deg'})`,
            border: '3px solid white',
          }}>
            {indicator.text}
          </div>
        )}

        {/* Character */}
        <div className="card-char-front" style={{
          fontWeight: '400',
          color: '#0f172a',
          lineHeight: 1,
          textShadow: '2px 2px 0 rgba(0,0,0,0.05)',
        }}>
          {currentCard.char}
        </div>

        {/* Frequency level badge */}
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          background: `${freq.color}20`,
          border: `1px solid ${freq.color}40`,
          borderRadius: '12px',
        }}>
          <span style={{
            fontSize: '11px',
            fontWeight: '700',
            color: freq.color,
          }}>{freq.label}</span>
          <span style={{
            fontSize: '10px',
            color: '#64748b',
          }}>#{currentCard.originalIndex + 1}</span>
        </div>

        {/* Rank badge */}
        <div style={{
          position: 'absolute',
          bottom: '24px',
          padding: '6px 16px',
          background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
          color: 'white',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: '600',
          letterSpacing: '0.5px',
        }}>
          {currentIndex + 1} / {deckLength}
        </div>

        {/* Tap hint */}
        <div style={{
          position: 'absolute',
          bottom: '70px',
          color: '#94a3b8',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          <span style={{ fontSize: '16px' }}>👆</span> 点击翻转
        </div>
      </div>

      {/* Back of card */}
      <div style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        backfaceVisibility: 'hidden',
        transform: 'rotateY(180deg)',
        background: isDarkMode
          ? 'linear-gradient(145deg, #0f172a, #1e293b)'
          : 'linear-gradient(145deg, #f0f9f8, #e0f2f1)',
        borderRadius: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        boxShadow: isDarkMode
          ? '0 25px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)'
          : '0 25px 80px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.8)',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
      }}>
        {/* Frequency badge on back */}
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          background: `${freq.color}30`,
          border: `1px solid ${freq.color}50`,
          borderRadius: '12px',
        }}>
          <span style={{
            fontSize: '11px',
            fontWeight: '700',
            color: freq.color,
          }}>{freq.label}</span>
          <span style={{
            fontSize: '10px',
            color: '#94a3b8',
          }}>#{currentCard.originalIndex + 1}</span>
        </div>

        {/* Character (smaller on back) */}
        <div className="card-char-back" style={{
          color: isDarkMode ? '#f8fafc' : '#1a1a2e',
          marginBottom: '12px',
          lineHeight: 1,
        }}>
          {currentCard.char}
        </div>

        {/* Pinyin */}
        <div className="card-pinyin" style={{
          color: isDarkMode ? '#3b82f6' : '#4FAEA5',
          fontWeight: '500',
          marginBottom: '8px',
          fontStyle: 'italic',
        }}>
          {currentCard.pinyin}
        </div>

        {/* Meaning */}
        <div className="card-meaning" style={{
          color: isDarkMode ? '#e2e8f0' : '#374151',
          textAlign: 'center',
          marginBottom: '16px',
          lineHeight: 1.4,
          maxWidth: '100%',
        }}>
          {currentCard.meaning}
        </div>

        {/* Divider */}
        <div style={{
          width: '60%',
          height: '1px',
          background: isDarkMode
            ? 'linear-gradient(90deg, transparent, #475569, transparent)'
            : 'linear-gradient(90deg, transparent, #9ca3af, transparent)',
          marginBottom: '16px',
        }} />

        {/* Example */}
        <div className="card-example" style={{
          color: isDarkMode ? '#94a3b8' : '#6b7280',
          textAlign: 'center',
          lineHeight: 1.5,
          padding: '0 4px',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          <span style={{ color: '#22c55e', fontWeight: '500' }}>例:</span> {currentCard.example}
        </div>
      </div>
    </div>
  );
});

FlashCard.displayName = 'FlashCard';

export default FlashCard;
